<script setup lang="ts">
type PopupColor = "white" | "elevated";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    error?: string;
    color?: PopupColor;
    collapsible?: boolean;
  }>(),
  { error: undefined, color: "white", collapsible: false }
);

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const isMobile = useIsMobile();
const isOpen = ref(true);
const sheetOpen = ref(false);

// The scroll container lives here (desktop: the content div; mobile: the bottom sheet), so an infinite
// list rendered in #content gets it via `getRoot`. Exposed as a computed → resolves per breakpoint.
const sheetRef = useTemplateRef<{ scrollEl: HTMLElement | null }>("sheetRef");
const scrollContentEl = useTemplateRef<HTMLElement>("scrollContentEl");
const scrollEl = computed<HTMLElement | null>(() =>
  isMobile.value ? (sheetRef.value?.scrollEl ?? null) : (scrollContentEl.value ?? null)
);
defineExpose({ scrollEl });

const bgClass = computed(
  () =>
    ({
      white: "bg-white",
      elevated: "bg-elevated",
    })[props.color]
);

// Échap : sur desktop, géré ici ; sur mobile, c'est AppMapSheet qui l'émet (→ onSheetClose).
useAppEscapeKey(() => emit("close"), { when: computed(() => !isMobile.value) });

// Feuille mobile (AppMapSheet) : ouverte après le montage pour jouer la transition d'entrée ; la
// fermeture (bouton / swipe / Échap) lance la sortie puis laisse l'hôte démonter le popup après 300 ms.
onMounted(() => {
  sheetOpen.value = true;
});

function onSheetClose() {
  sheetOpen.value = false;
  setTimeout(() => emit("close"), 300);
}
</script>

<template>
  <!-- Mobile : feuille non-modale AppMapSheet (carte manipulable dessous). Le slot par défaut sert de
       titre (présentation structurée, avec #content) ou de contenu (présentation simple). -->
  <AppMapSheet v-if="isMobile" ref="sheetRef" :open="sheetOpen" @close="onSheetClose">
    <WebMapPopupBody :loading="loading" :error="error">
      <template v-if="$slots.loading" #loading><slot name="loading" /></template>
      <slot />
    </WebMapPopupBody>
    <template v-if="$slots.content && $slots.toggle" #toggle><slot name="toggle" /></template>
    <template v-if="$slots.content" #content>
      <slot v-if="!loading && !error" name="content" />
    </template>
  </AppMapSheet>

  <div
    v-else
    :class="bgClass"
    class="group/popup max-h-full rounded-lg overflow-hidden shadow-medium w-[clamp(16rem,30%,24rem)] max-w-full flex flex-col"
  >
    <header class="flex gap-x-2 p-4 shrink-0">
      <div class="grow">
        <WebMapPopupBody :loading="loading" :error="error">
          <template v-if="$slots.loading" #loading><slot name="loading" /></template>
          <slot />
        </WebMapPopupBody>
      </div>
      <UButton
        square
        variant="ghost"
        icon="icon:close-small"
        size="lg"
        class="shrink-0 self-start -mt-3 -mr-3 rounded-md"
        :ui="{ base: color !== 'white' ? 'hover:not-disabled:bg-white' : '' }"
        :aria-label="$t('ui.close')"
        @click="emit('close')"
      />
    </header>
    <div v-if="collapsible && $slots.content" class="shrink-0 px-4 -mt-3">
      <div v-if="$slots.toggle" class="pb-2 pt-6">
        <hr class="border-blue-400" />
      </div>
      <div class="flex" :class="[$slots.toggle ? 'pb-2' : 'pb-1']">
        <UButton
          @click="() => { isOpen = !isOpen }"
          variant="ghost"
          class="group/button grow hover:bg-transparent! px-0 py-1 flex justify-start -mx-2"
        >
          <span v-if="$slots.toggle" class="grow text-left px-2">
            <slot name="toggle" />
          </span>
          <span class="shrink-0 flex p-2 rounded">
            <UIcon name="icon:chevronDown" class="size-4 transition-transform" :class="{ 'rotate-180': isOpen }" />
          </span>
        </UButton>
      </div>
    </div>
    <div v-if="$slots.content && isOpen" ref="scrollContentEl" class="flex-1 min-h-0 overflow-y-auto scroll-smooth bg-white">
      <slot name="content" />
    </div>
  </div>
</template>
